import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BoxArrowUpRight,
  CheckLg,
  Pencil,
  PersonDash,
  PersonPlus,
  Plus,
  ShieldCheck,
  Trash,
  XLg,
} from "react-bootstrap-icons";
import { fetchCatalogs } from "../api";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { Modal } from "../components/ui/Modal";
import { Tabs, type TabItem } from "../components/ui/Tabs";
import { Alert } from "../components/ui/Alert";
import { Badge, approvalTone } from "../components/ui/Badge";
import { Field, Input, Select, Switch } from "../components/ui/Field";
import {
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  TableEmpty,
} from "../components/ui/Table";
import { useDialogs } from "../components/ui/dialogs";
import { cn } from "../lib/cn";

type User = {
  id: string;
  username: string;
  email?: string;
  role: string;
  isAutoApproved?: boolean;
  receiveApprovalEmails?: boolean;
  groups?: { id: string }[];
};
type Group = {
  id: string;
  name: string;
  _count?: { users: number };
  users?: { id: string; username: string }[];
  permissions?: GroupPermission[];
};
type UserPermission = {
  id: string;
  path: string;
  level: string;
  user: { username: string };
};
type GroupPermission = { id: string; path: string; level: string };
type CatalogNode = { name: string; type: "folder" | "file"; children?: CatalogNode[] };
type FolderInfo = { path: string; name: string };
type DiagramApproval = {
  id: string;
  path: string;
  status: string;
  updatedAt: string;
  requester: { username: string };
  reviewer?: { username: string };
};

const API_URL = import.meta.env.VITE_API_URL || "";

const getFolders = (nodes: CatalogNode[], parentPath = ""): FolderInfo[] => {
  let folders: FolderInfo[] = [];
  if (parentPath === "") folders.push({ path: ".", name: "Catalog root" });

  for (const node of nodes) {
    const currentPath = parentPath ? `${parentPath}/${node.name}` : node.name;
    if (node.type === "folder") {
      folders.push({ path: currentPath, name: currentPath });
      if (node.children) folders = folders.concat(getFolders(node.children, currentPath));
    }
  }
  return folders;
};

const ROLE_OPTIONS = [
  { value: "user", label: "User" },
  { value: "approver", label: "Approver" },
  { value: "admin", label: "Admin" },
];

const roleTone = (role: string) =>
  role === "admin" ? "accent" : role === "approver" ? "warning" : "neutral";

export default function AdminPage({ token, role }: { token: string; role?: string }) {
  const isAdmin = role === "admin";
  const dialogs = useDialogs();

  const [users, setUsers] = useState<User[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [userPermissions, setUserPermissions] = useState<UserPermission[]>([]);
  const [catalog, setCatalog] = useState<CatalogNode[]>([]);
  const [approvals, setApprovals] = useState<DiagramApproval[]>([]);

  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [newGroupName, setNewGroupName] = useState("");
  const [showRenameGroupModal, setShowRenameGroupModal] = useState(false);

  const [groupPathToAssign, setGroupPathToAssign] = useState(".");
  const [groupLevelToAssign, setGroupLevelToAssign] = useState("viewer");
  const [userToAddToGroup, setUserToAddToGroup] = useState("");

  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editUserGroupIds, setEditUserGroupIds] = useState<string[]>([]);
  const [newRole, setNewRole] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [isAutoApproved, setIsAutoApproved] = useState(false);
  const [receiveApprovalEmails, setReceiveApprovalEmails] = useState(false);

  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedPath, setSelectedPath] = useState(".");
  const [selectedLevel, setSelectedLevel] = useState("viewer");

  const [createUsername, setCreateUsername] = useState("");
  const [createEmail, setCreateEmail] = useState("");
  const [createPassword, setCreatePassword] = useState("");
  const [createRole, setCreateRole] = useState("user");
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);

  const [ssoMappings, setSsoMappings] = useState<
    {
      id: string;
      entraGroupId: string;
      /** Cached from Graph on SSO login; null until a member of that group signs in. */
      entraGroupName?: string | null;
      path: string;
      level: string;
    }[]
  >([]);
  const [newMapping, setNewMapping] = useState({
    entraGroupId: "",
    entraGroupName: "",
    path: ".",
    level: "viewer",
  });

  // Harvested from SSO logins, not read from Graph. See the EntraGroup model.
  const [entraGroups, setEntraGroups] = useState<
    { entraGroupId: string; displayName: string }[]
  >([]);

  const [audit, setAudit] = useState<
    {
      id: string;
      at: string;
      actor: string;
      action: string;
      subjectType: string;
      subject: string;
      path: string | null;
      levelBefore: string | null;
      levelAfter: string | null;
      source: string | null;
      detail: string | null;
    }[]
  >([]);
  const [auditFilter, setAuditFilter] = useState("");

  const [sendGridSettings, setSendGridSettings] = useState({ apiKey: "", fromEmail: "" });
  const [sendGridSaved, setSendGridSaved] = useState(false);

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );
  const jsonHeaders = useMemo(
    () => ({ "Content-Type": "application/json", ...authHeaders }),
    [authHeaders],
  );

  const fetchData = useCallback(async () => {
    try {
      const pApprovals = fetch(`${API_URL}/api/approvals`, { headers: authHeaders });

      // Approvers only get the queue; everything else is admin-only.
      if (!isAdmin) {
        setApprovals(await (await pApprovals).json());
        return;
      }

      const [usersRes, userPermsRes, groupsRes, catRes, mappingsRes, sgRes, appRes, egRes, auditRes] =
        await Promise.all([
          fetch(`${API_URL}/api/users`, { headers: authHeaders }),
          fetch(`${API_URL}/api/permissions`, { headers: authHeaders }),
          fetch(`${API_URL}/api/groups`, { headers: authHeaders }),
          fetchCatalogs(token),
          fetch(`${API_URL}/api/admin/sso-group-mappings`, { headers: authHeaders }),
          fetch(`${API_URL}/api/admin/sendgrid`, { headers: authHeaders }),
          pApprovals,
          fetch(`${API_URL}/api/admin/entra-groups`, { headers: authHeaders }),
          fetch(`${API_URL}/api/admin/audit?take=300`, { headers: authHeaders }),
        ]);

      setUsers(await usersRes.json());
      setUserPermissions(await userPermsRes.json());
      setGroups(await groupsRes.json());
      setCatalog(catRes);
      setSsoMappings(await mappingsRes.json());
      setSendGridSettings(await sgRes.json());
      setApprovals(await appRes.json());
      // These two are additions; an older server returns 404 and the page
      // should still work rather than rendering nothing.
      if (egRes.ok) setEntraGroups(await egRes.json());
      if (auditRes.ok) setAudit(await auditRes.json());
    } catch (error) {
      console.error("Failed to fetch admin data:", error);
    }
  }, [authHeaders, isAdmin, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!currentUser) return;
    setEditUserGroupIds(currentUser.groups?.map((g) => g.id) || []);
    setIsAutoApproved(currentUser.isAutoApproved || false);
    setReceiveApprovalEmails(currentUser.receiveApprovalEmails || false);
  }, [currentUser]);

  const folderOptions = useMemo(() => getFolders(catalog), [catalog]);
  const pendingCount = approvals.filter((a) => a.status === "PENDING").length;

  /* --- Handlers ---------------------------------------------------------- */

  const handleReviewApproval = async (
    id: string,
    action: "APPROVE" | "DENY" | "AUTOAPPROVE",
  ) => {
    await fetch(`${API_URL}/api/approvals/review`, {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify({ id, action }),
    });
    fetchData();
  };

  const handleSaveSendGrid = async (e: React.FormEvent) => {
    e.preventDefault();
    await fetch(`${API_URL}/api/admin/sendgrid`, {
      method: "PUT",
      headers: jsonHeaders,
      body: JSON.stringify(sendGridSettings),
    });
    setSendGridSaved(true);
    setTimeout(() => setSendGridSaved(false), 4000);
  };

  const handleCreateMapping = async (e: React.FormEvent) => {
    e.preventDefault();
    await fetch(`${API_URL}/api/admin/sso-group-mappings`, {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify(newMapping),
    });
    setNewMapping({ entraGroupId: "", entraGroupName: "", path: ".", level: "viewer" });
    fetchData();
  };

  const handleDeleteMapping = async (id: string) => {
    const confirmed = await dialogs.confirm({
      title: "Delete this SSO mapping?",
      description:
        "Users who receive access through this Entra group will lose it on their next sign-in.",
      confirmLabel: "Delete mapping",
      tone: "danger",
    });
    if (!confirmed) return;

    await fetch(`${API_URL}/api/admin/sso-group-mappings/${id}`, {
      method: "DELETE",
      headers: authHeaders,
    });
    fetchData();
  };

  const handleSelectGroup = async (group: Group) => {
    const res = await fetch(`${API_URL}/api/groups/${group.id}`, {
      headers: authHeaders,
    });
    if (!res.ok) return;
    setSelectedGroup(await res.json());
  };

  const handleCreateGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newGroupName.trim()) return;
    await fetch(`${API_URL}/api/groups`, {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify({ name: newGroupName.trim() }),
    });
    setNewGroupName("");
    fetchData();
  };

  const handleRenameGroup = async () => {
    if (!selectedGroup || !selectedGroup.name.trim()) return;
    await fetch(`${API_URL}/api/groups/${selectedGroup.id}`, {
      method: "PUT",
      headers: jsonHeaders,
      body: JSON.stringify({ name: selectedGroup.name.trim() }),
    });
    setShowRenameGroupModal(false);
    fetchData();
    setSelectedGroup(null);
  };

  const handleDeleteGroup = async (group: Group) => {
    const confirmed = await dialogs.confirm({
      title: `Delete the group "${group.name}"?`,
      description:
        "Members will lose every permission granted through this group. This cannot be undone.",
      confirmLabel: "Delete group",
      tone: "danger",
    });
    if (!confirmed) return;

    await fetch(`${API_URL}/api/groups/${group.id}`, {
      method: "DELETE",
      headers: authHeaders,
    });
    setSelectedGroup(null);
    fetchData();
  };

  const handleAddUserToGroup = async () => {
    if (!selectedGroup || !userToAddToGroup) return;
    const user = users.find((u) => u.id === userToAddToGroup);
    if (!user) return;

    const newGroupIds = [...(user.groups?.map((g) => g.id) || []), selectedGroup.id];
    await fetch(`${API_URL}/api/users/${user.id}`, {
      method: "PUT",
      headers: jsonHeaders,
      body: JSON.stringify({ groupIds: newGroupIds }),
    });
    setUserToAddToGroup("");
    handleSelectGroup(selectedGroup);
    fetchData();
  };

  const handleRemoveUserFromGroup = async (userId: string) => {
    if (!selectedGroup) return;
    const user = users.find((u) => u.id === userId);
    if (!user) return;

    const newGroupIds =
      user.groups?.map((g) => g.id).filter((id) => id !== selectedGroup.id) || [];
    await fetch(`${API_URL}/api/users/${userId}`, {
      method: "PUT",
      headers: jsonHeaders,
      body: JSON.stringify({ groupIds: newGroupIds }),
    });
    handleSelectGroup(selectedGroup);
    fetchData();
  };

  const handleAssignGroupPermission = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedGroup) return;
    await fetch(`${API_URL}/api/permissions/group`, {
      method: "PUT",
      headers: jsonHeaders,
      body: JSON.stringify({
        groupId: selectedGroup.id,
        path: groupPathToAssign,
        level: groupLevelToAssign,
      }),
    });
    handleSelectGroup(selectedGroup);
  };

  const handleDeleteGroupPermission = async (permissionId: string) => {
    const confirmed = await dialogs.confirm({
      title: "Remove this permission?",
      description: "Members of this group will lose the access it grants.",
      confirmLabel: "Remove permission",
      tone: "danger",
    });
    if (!confirmed) return;

    await fetch(`${API_URL}/api/permissions/group/${permissionId}`, {
      method: "DELETE",
      headers: authHeaders,
    });
    if (selectedGroup) handleSelectGroup(selectedGroup);
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateLoading(true);
    setCreateError(null);
    setCreateSuccess(null);
    try {
      const res = await fetch(`${API_URL}/api/users`, {
        method: "POST",
        headers: jsonHeaders,
        body: JSON.stringify({
          username: createUsername,
          email: createEmail,
          password: createPassword,
          role: createRole,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to create user.");

      setCreateSuccess(`Created ${data.username}.`);
      setCreateUsername("");
      setCreateEmail("");
      setCreatePassword("");
      setCreateRole("user");
      fetchData();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "An unknown error occurred.");
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEditUser = async () => {
    if (!currentUser) return;
    const body: Record<string, unknown> = {};
    if (newRole) body.role = newRole;
    body.email = newEmail; // Sent unconditionally so it can be cleared.
    if (newPassword) body.password = newPassword;
    body.groupIds = editUserGroupIds;
    body.isAutoApproved = isAutoApproved;
    body.receiveApprovalEmails = receiveApprovalEmails;

    await fetch(`${API_URL}/api/users/${currentUser.id}`, {
      method: "PUT",
      headers: jsonHeaders,
      body: JSON.stringify(body),
    });
    setShowEditModal(false);
    setNewPassword("");
    fetchData();
  };

  const handleDeleteUser = async () => {
    if (!currentUser) return;
    await fetch(`${API_URL}/api/users/${currentUser.id}`, {
      method: "DELETE",
      headers: authHeaders,
    });
    setShowDeleteModal(false);
    fetchData();
  };

  const handleAssignUserPermission = async (e: React.FormEvent) => {
    e.preventDefault();
    await fetch(`${API_URL}/api/permissions`, {
      method: "PUT",
      headers: jsonHeaders,
      body: JSON.stringify({
        userId: selectedUserId,
        path: selectedPath,
        level: selectedLevel,
      }),
    });
    fetchData();
  };

  const handleDeleteUserPermission = async (permissionId: string) => {
    const confirmed = await dialogs.confirm({
      title: "Remove this permission?",
      description: "The user will lose the access it grants.",
      confirmLabel: "Remove permission",
      tone: "danger",
    });
    if (!confirmed) return;

    await fetch(`${API_URL}/api/permissions`, {
      method: "DELETE",
      headers: jsonHeaders,
      body: JSON.stringify({ permissionId }),
    });
    fetchData();
  };

  /* --- Tabs -------------------------------------------------------------- */

  const approvalsTab: TabItem = {
    key: "approvals",
    label: "Sync approvals",
    count: pendingCount,
    content: (
      <Card
        title="Approval queue"
        description="Requests to publish a diagram to the Azure DevOps wiki."
        flush
      >
        <Table>
          <THead>
            <TR className="hover:bg-transparent">
              <TH>Diagram</TH>
              <TH>Status</TH>
              <TH>Requester</TH>
              <TH>Reviewer</TH>
              <TH>Updated</TH>
              <TH className="text-right">Action</TH>
            </TR>
          </THead>
          <TBody>
            {approvals.map((app) => (
              <TR key={app.id}>
                <TD>
                  <a
                    href={`/?file=${encodeURIComponent(app.path)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-accent hover:text-accent-hover inline-flex items-center gap-1.5 font-medium no-underline hover:underline"
                  >
                    {app.path}
                    <BoxArrowUpRight size={10} aria-hidden="true" />
                  </a>
                </TD>
                <TD>
                  <Badge tone={approvalTone(app.status)}>{app.status}</Badge>
                </TD>
                <TD className="text-ink-muted">{app.requester?.username}</TD>
                <TD className="text-ink-muted">{app.reviewer?.username || "—"}</TD>
                <TD className="text-ink-muted whitespace-nowrap tabular-nums">
                  {new Date(app.updatedAt).toLocaleString()}
                </TD>
                <TD>
                  {app.status === "PENDING" && (
                    <div className="flex flex-wrap justify-end gap-1.5">
                      <Button
                        variant="success"
                        onClick={() => handleReviewApproval(app.id, "APPROVE")}
                        title="Approve this version"
                      >
                        <CheckLg size={13} />
                        Approve
                      </Button>
                      <Button
                        onClick={() => handleReviewApproval(app.id, "AUTOAPPROVE")}
                        title="Approve and exempt this diagram from future reviews"
                      >
                        Always approve
                      </Button>
                      <Button
                        variant="danger"
                        onClick={() => handleReviewApproval(app.id, "DENY")}
                        title="Deny this sync request"
                      >
                        <XLg size={13} />
                        Deny
                      </Button>
                    </div>
                  )}
                </TD>
              </TR>
            ))}
            {approvals.length === 0 && (
              <TableEmpty colSpan={6}>No sync requests yet.</TableEmpty>
            )}
          </TBody>
        </Table>
      </Card>
    ),
  };

  const usersTab: TabItem = {
    key: "users",
    label: "Users",
    count: users.length,
    content: (
      <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
        <Card title="Existing users" flush>
          <Table>
            <THead>
              <TR className="hover:bg-transparent">
                <TH>Username</TH>
                <TH>Email</TH>
                <TH>Role</TH>
                <TH className="text-right">Actions</TH>
              </TR>
            </THead>
            <TBody>
              {users.map((user) => (
                <TR key={user.id}>
                  <TD className="font-medium">{user.username}</TD>
                  <TD className="text-ink-muted">
                    {user.email || <span className="text-ink-subtle">Not set</span>}
                  </TD>
                  <TD>
                    <Badge tone={roleTone(user.role)}>{user.role}</Badge>
                  </TD>
                  <TD>
                    <div className="flex justify-end gap-1.5">
                      <Button
                        iconOnly
                        variant="ghost"
                        title={`Edit ${user.username}`}
                        aria-label={`Edit ${user.username}`}
                        onClick={() => {
                          setCurrentUser(user);
                          setNewRole(user.role);
                          setNewEmail(user.email || "");
                          setNewPassword("");
                          setShowEditModal(true);
                        }}
                      >
                        <Pencil size={12} />
                      </Button>
                      <Button
                        iconOnly
                        variant="ghost"
                        className="hover:bg-danger-soft hover:text-danger"
                        title={`Delete ${user.username}`}
                        aria-label={`Delete ${user.username}`}
                        onClick={() => {
                          setCurrentUser(user);
                          setShowDeleteModal(true);
                        }}
                      >
                        <Trash size={12} />
                      </Button>
                    </div>
                  </TD>
                </TR>
              ))}
              {users.length === 0 && <TableEmpty colSpan={4}>No users yet.</TableEmpty>}
            </TBody>
          </Table>
        </Card>

        <Card title="Create a user" description="Local accounts only — SSO users are provisioned on first sign-in.">
          <form onSubmit={handleCreateUser} className="space-y-4">
            {createError && <Alert tone="danger">{createError}</Alert>}
            {createSuccess && <Alert tone="success">{createSuccess}</Alert>}

            <Field label="Username">
              {(props) => (
                <Input
                  {...props}
                  value={createUsername}
                  onChange={(e) => setCreateUsername(e.target.value)}
                  placeholder="j.doe"
                  autoComplete="off"
                  required
                />
              )}
            </Field>

            <Field label="Email" hint="Optional. Used for approval notifications.">
              {(props) => (
                <Input
                  {...props}
                  type="email"
                  value={createEmail}
                  onChange={(e) => setCreateEmail(e.target.value)}
                  placeholder="j.doe@company.com"
                  autoComplete="off"
                />
              )}
            </Field>

            <Field label="Temporary password">
              {(props) => (
                <Input
                  {...props}
                  type="password"
                  value={createPassword}
                  onChange={(e) => setCreatePassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              )}
            </Field>

            <Field label="Role">
              {(props) => (
                <Select
                  {...props}
                  value={createRole}
                  onChange={(e) => setCreateRole(e.target.value)}
                >
                  {ROLE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Button type="submit" variant="primary" size="md" disabled={createLoading}>
              {createLoading ? (
                <span
                  aria-hidden="true"
                  className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/35 border-t-white"
                />
              ) : (
                <PersonPlus size={14} />
              )}
              {createLoading ? "Creating" : "Create user"}
            </Button>
          </form>
        </Card>
      </div>
    ),
  };

  const groupsTab: TabItem = {
    key: "groups",
    label: "Groups",
    count: groups.length,
    content: (
      <div className="grid gap-5 lg:grid-cols-[320px_1fr]">
        <div className="flex flex-col gap-5">
          <Card title="New group">
            <form onSubmit={handleCreateGroup} className="flex gap-2">
              <Input
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="Group name"
                aria-label="New group name"
              />
              <Button type="submit" variant="primary" size="md" iconOnly aria-label="Create group">
                <Plus size={16} />
              </Button>
            </form>
          </Card>

          <Card title="All groups" flush>
            <ul className="divide-line divide-y">
              {groups.map((group) => {
                const isSelected = selectedGroup?.id === group.id;
                return (
                  <li key={group.id}>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => handleSelectGroup(group)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          handleSelectGroup(group);
                        }
                      }}
                      className={cn(
                        "group flex cursor-pointer items-center gap-2 px-4 py-2.5",
                        "transition-colors duration-(--duration-instant) ease-(--ease-standard)",
                        isSelected
                          ? "bg-accent-soft text-accent font-semibold"
                          : "text-ink hover:bg-surface-hover",
                      )}
                    >
                      <span className="min-w-0 flex-1 truncate text-[13px]">
                        {group.name}
                      </span>

                      <Badge tone={isSelected ? "accent" : "neutral"}>
                        {group._count?.users ?? 0}
                      </Badge>

                      <span
                        className="flex shrink-0 gap-0.5 opacity-0 transition-opacity duration-(--duration-fast) group-hover:opacity-100 group-focus-within:opacity-100"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <Button
                          size="xs"
                          variant="ghost"
                          iconOnly
                          title={`Rename ${group.name}`}
                          aria-label={`Rename ${group.name}`}
                          onClick={() => {
                            handleSelectGroup(group);
                            setShowRenameGroupModal(true);
                          }}
                        >
                          <Pencil size={11} />
                        </Button>
                        <Button
                          size="xs"
                          variant="ghost"
                          iconOnly
                          className="hover:bg-danger-soft hover:text-danger"
                          title={`Delete ${group.name}`}
                          aria-label={`Delete ${group.name}`}
                          onClick={() => handleDeleteGroup(group)}
                        >
                          <Trash size={11} />
                        </Button>
                      </span>
                    </div>
                  </li>
                );
              })}
              {groups.length === 0 && (
                <li className="text-ink-subtle px-4 py-10 text-center text-[13px]">
                  No groups yet.
                </li>
              )}
            </ul>
          </Card>
        </div>

        {selectedGroup ? (
          <div className="flex flex-col gap-5">
            <Card title={`Members of ${selectedGroup.name}`} flush>
              <Table>
                <THead>
                  <TR className="hover:bg-transparent">
                    <TH>Member</TH>
                    <TH className="text-right">Action</TH>
                  </TR>
                </THead>
                <TBody>
                  {selectedGroup.users?.map((user) => (
                    <TR key={user.id}>
                      <TD className="font-medium">{user.username}</TD>
                      <TD>
                        <div className="flex justify-end">
                          <Button
                            variant="ghost"
                            className="hover:bg-danger-soft hover:text-danger"
                            onClick={() => handleRemoveUserFromGroup(user.id)}
                          >
                            <PersonDash size={13} />
                            Remove
                          </Button>
                        </div>
                      </TD>
                    </TR>
                  ))}
                  {!selectedGroup.users?.length && (
                    <TableEmpty colSpan={2}>This group has no members.</TableEmpty>
                  )}
                </TBody>
              </Table>

              <div className="border-line flex items-end gap-2 border-t px-4 py-3.5">
                <Field label="Add a member" className="flex-1">
                  {(props) => (
                    <Select
                      {...props}
                      value={userToAddToGroup}
                      onChange={(e) => setUserToAddToGroup(e.target.value)}
                    >
                      <option value="">Select a user…</option>
                      {users
                        .filter((u) => !selectedGroup.users?.some((m) => m.id === u.id))
                        .map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.username}
                          </option>
                        ))}
                    </Select>
                  )}
                </Field>
                <Button
                  size="md"
                  variant="primary"
                  onClick={handleAddUserToGroup}
                  disabled={!userToAddToGroup}
                >
                  Add
                </Button>
              </div>
            </Card>

            <Card title={`Permissions for ${selectedGroup.name}`} flush>
              <form
                onSubmit={handleAssignGroupPermission}
                className="border-line flex items-end gap-2 border-b px-4 py-3.5"
              >
                <Field label="Folder" className="flex-1">
                  {(props) => (
                    <Select
                      {...props}
                      value={groupPathToAssign}
                      onChange={(e) => setGroupPathToAssign(e.target.value)}
                    >
                      {folderOptions.map((f) => (
                        <option key={f.path} value={f.path}>
                          {f.name}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
                <Field label="Level" className="w-32 shrink-0">
                  {(props) => (
                    <Select
                      {...props}
                      value={groupLevelToAssign}
                      onChange={(e) => setGroupLevelToAssign(e.target.value)}
                    >
                      <option value="viewer">Viewer</option>
                      <option value="editor">Editor</option>
                      <option value="deny">Deny (blocks inherited access)</option>
                    </Select>
                  )}
                </Field>
                <Button type="submit" size="md" variant="primary">
                  Assign
                </Button>
              </form>

              <Table>
                <THead>
                  <TR className="hover:bg-transparent">
                    <TH>Path</TH>
                    <TH>Level</TH>
                    <TH className="text-right">Action</TH>
                  </TR>
                </THead>
                <TBody>
                  {selectedGroup.permissions?.map((p) => (
                    <TR key={p.id}>
                      <TD className="font-medium">{p.path}</TD>
                      <TD>
                        <Badge tone={p.level === "deny" ? "danger" : p.level === "editor" ? "accent" : "neutral"}>
                          {p.level}
                        </Badge>
                      </TD>
                      <TD>
                        <div className="flex justify-end">
                          <Button
                            iconOnly
                            variant="ghost"
                            className="hover:bg-danger-soft hover:text-danger"
                            title="Remove permission"
                            aria-label={`Remove ${p.level} on ${p.path}`}
                            onClick={() => handleDeleteGroupPermission(p.id)}
                          >
                            <Trash size={12} />
                          </Button>
                        </div>
                      </TD>
                    </TR>
                  ))}
                  {!selectedGroup.permissions?.length && (
                    <TableEmpty colSpan={3}>No permissions assigned.</TableEmpty>
                  )}
                </TBody>
              </Table>
            </Card>
          </div>
        ) : (
          <div className="border-line flex min-h-64 items-center justify-center rounded-xl border border-dashed p-8">
            <div className="animate-fade-in text-center">
              <ShieldCheck size={22} className="text-ink-subtle/50 mx-auto mb-2.5" />
              <p className="text-ink-muted text-[13px] font-medium">No group selected</p>
              <p className="text-ink-subtle mt-1 text-xs">
                Pick a group to manage its members and permissions.
              </p>
            </div>
          </div>
        )}
      </div>
    ),
  };

  const permissionsTab: TabItem = {
    key: "permissions",
    label: "Direct permissions",
    count: userPermissions.length,
    content: (
      <div className="grid gap-5 lg:grid-cols-[340px_1fr]">
        <Card
          title="Assign a permission"
          description="Direct grants apply on top of any group permissions."
        >
          <form onSubmit={handleAssignUserPermission} className="space-y-4">
            <Field label="User">
              {(props) => (
                <Select
                  {...props}
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  required
                >
                  <option value="">Select a user…</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.username}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field label="Folder">
              {(props) => (
                <Select
                  {...props}
                  value={selectedPath}
                  onChange={(e) => setSelectedPath(e.target.value)}
                >
                  {folderOptions.map((f) => (
                    <option key={f.path} value={f.path}>
                      {f.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field label="Level">
              {(props) => (
                <Select
                  {...props}
                  value={selectedLevel}
                  onChange={(e) => setSelectedLevel(e.target.value)}
                >
                  <option value="viewer">Viewer</option>
                  <option value="editor">Editor</option>
                  <option value="deny">Deny (blocks inherited access)</option>
                </Select>
              )}
            </Field>

            <Button type="submit" size="md" variant="primary">
              Assign permission
            </Button>
          </form>
        </Card>

        <Card title="Existing direct permissions" flush>
          <Table>
            <THead>
              <TR className="hover:bg-transparent">
                <TH>User</TH>
                <TH>Path</TH>
                <TH>Level</TH>
                <TH className="text-right">Action</TH>
              </TR>
            </THead>
            <TBody>
              {userPermissions.map((p) => (
                <TR key={p.id}>
                  <TD className="font-medium">{p.user.username}</TD>
                  <TD className="text-ink-muted">{p.path}</TD>
                  <TD>
                    <Badge tone={p.level === "deny" ? "danger" : p.level === "editor" ? "accent" : "neutral"}>
                      {p.level}
                    </Badge>
                  </TD>
                  <TD>
                    <div className="flex justify-end">
                      <Button
                        iconOnly
                        variant="ghost"
                        className="hover:bg-danger-soft hover:text-danger"
                        title="Remove permission"
                        aria-label={`Remove ${p.level} on ${p.path} from ${p.user.username}`}
                        onClick={() => handleDeleteUserPermission(p.id)}
                      >
                        <Trash size={12} />
                      </Button>
                    </div>
                  </TD>
                </TR>
              ))}
              {userPermissions.length === 0 && (
                <TableEmpty colSpan={4}>No direct permissions assigned.</TableEmpty>
              )}
            </TBody>
          </Table>
        </Card>
      </div>
    ),
  };

  const ssoTab: TabItem = {
    key: "sso-mappings",
    label: "SSO mappings",
    count: ssoMappings.length,
    content: (
      <div className="grid gap-5 lg:grid-cols-[340px_1fr]">
        <Card
          title="New mapping"
          description="Grants catalog access to everyone in an Entra ID group."
        >
          <form onSubmit={handleCreateMapping} className="space-y-4">
            {/* Groups are harvested from SSO logins rather than read from
                Microsoft Graph, which would need Group.Read.All as an
                application permission with tenant admin consent. The trade-off
                is that only groups somebody has signed in with are listed, so
                the Object ID field stays available underneath. */}
            <Field
              label="Entra group"
              hint={
                entraGroups.length
                  ? `${entraGroups.length} group(s) seen so far from sign-ins. Not listed? Paste the Object ID below.`
                  : "No groups seen yet. They appear here as people sign in; until then, paste an Object ID below."
              }
            >
              {(props) => (
                <Select
                  {...props}
                  value={
                    entraGroups.some((g) => g.entraGroupId === newMapping.entraGroupId)
                      ? newMapping.entraGroupId
                      : ""
                  }
                  onChange={(e) => {
                    const g = entraGroups.find((x) => x.entraGroupId === e.target.value);
                    setNewMapping((prev) => ({
                      ...prev,
                      entraGroupId: e.target.value,
                      entraGroupName: g ? g.displayName : "",
                    }));
                  }}
                >
                  <option value="">— pick a group, or enter an ID below —</option>
                  {entraGroups.map((g) => (
                    <option key={g.entraGroupId} value={g.entraGroupId}>
                      {g.displayName}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field
              label="…or Entra group object ID"
              hint="Found in Entra ID under the group's Overview tab."
            >
              {(props) => (
                <Input
                  {...props}
                  value={newMapping.entraGroupId}
                  onChange={(e) =>
                    setNewMapping((prev) => ({
                      ...prev,
                      entraGroupId: e.target.value,
                      entraGroupName: "",
                    }))
                  }
                  placeholder="00000000-0000-0000-0000-000000000000"
                  className="font-mono text-xs"
                  required
                />
              )}
            </Field>

            <Field label="Folder">
              {(props) => (
                <Select
                  {...props}
                  value={newMapping.path}
                  onChange={(e) =>
                    setNewMapping((prev) => ({ ...prev, path: e.target.value }))
                  }
                >
                  {folderOptions.map((f) => (
                    <option key={f.path} value={f.path}>
                      {f.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field label="Level">
              {(props) => (
                <Select
                  {...props}
                  value={newMapping.level}
                  onChange={(e) =>
                    setNewMapping((prev) => ({ ...prev, level: e.target.value }))
                  }
                >
                  <option value="viewer">Viewer</option>
                  <option value="editor">Editor</option>
                  <option value="deny">Deny (blocks inherited access)</option>
                </Select>
              )}
            </Field>

            <Button type="submit" size="md" variant="primary">
              Create mapping
            </Button>
          </form>
        </Card>

        <Card title="Existing mappings" flush>
          <Table>
            <THead>
              <TR className="hover:bg-transparent">
                <TH>Entra group ID</TH>
                <TH>Path</TH>
                <TH>Level</TH>
                <TH className="text-right">Action</TH>
              </TR>
            </THead>
            <TBody>
              {ssoMappings.map((m) => (
                <TR key={m.id}>
                  {/* The name is cached from Graph when a member of the group
                      signs in, so it is blank until that first login. The id
                      stays visible underneath either way -- it is what the
                      mapping actually matches on. */}
                  <TD>
                    {m.entraGroupName ? (
                      <div className="flex flex-col">
                        <span>{m.entraGroupName}</span>
                        <span className="text-ink-muted font-mono text-[10px]">
                          {m.entraGroupId}
                        </span>
                      </div>
                    ) : (
                      <div className="flex flex-col">
                        <span className="text-ink-muted font-mono text-xs">
                          {m.entraGroupId}
                        </span>
                        <span className="text-ink-muted text-[10px] italic">
                          name resolves on first sign-in
                        </span>
                      </div>
                    )}
                  </TD>
                  <TD>{m.path}</TD>
                  <TD>
                    <Badge
                      tone={
                        m.level === "deny"
                          ? "danger"
                          : m.level === "editor"
                            ? "accent"
                            : "neutral"
                      }
                    >
                      {m.level}
                    </Badge>
                  </TD>
                  <TD>
                    <div className="flex justify-end">
                      <Button
                        iconOnly
                        variant="ghost"
                        className="hover:bg-danger-soft hover:text-danger"
                        title="Delete mapping"
                        aria-label={`Delete mapping for ${m.entraGroupId}`}
                        onClick={() => handleDeleteMapping(m.id)}
                      >
                        <Trash size={12} />
                      </Button>
                    </div>
                  </TD>
                </TR>
              ))}
              {ssoMappings.length === 0 && (
                <TableEmpty colSpan={4}>No SSO mappings configured.</TableEmpty>
              )}
            </TBody>
          </Table>
        </Card>
      </div>
    ),
  };

  const auditTab: TabItem = {
    key: "audit",
    label: "Audit",
    count: audit.length,
    content: (
      <Card
        title="Permission history"
        description={
          "Every change to who can reach what, including the ones the SSO sync makes " +
          "on its own. Append-only: nothing here can be edited or removed."
        }
      >
        <div className="mb-4 max-w-sm">
          <Field label="Filter" hint="Matches the account, group, path or administrator.">
            {(props) => (
              <Input
                {...props}
                value={auditFilter}
                onChange={(e) => setAuditFilter(e.target.value)}
                placeholder="e.g. HR, or a username"
              />
            )}
          </Field>
        </div>

        <Table>
          <THead>
            <TR>
              <TH>When</TH>
              <TH>Who</TH>
              <TH>Action</TH>
              <TH>Subject</TH>
              <TH>Path</TH>
              <TH>Change</TH>
            </TR>
          </THead>
          <TBody>
            {audit
              .filter((a) => {
                const q = auditFilter.trim().toLowerCase();
                if (!q) return true;
                return [a.actor, a.subject, a.path, a.action]
                  .filter(Boolean)
                  .some((v) => String(v).toLowerCase().includes(q));
              })
              .map((a) => (
                <TR key={a.id}>
                  <TD className="whitespace-nowrap text-xs">
                    {new Date(a.at).toLocaleString()}
                  </TD>
                  <TD>
                    {a.actor === "sso-reconciler" ? (
                      <Badge tone="neutral">SSO sync</Badge>
                    ) : (
                      a.actor
                    )}
                  </TD>
                  <TD className="text-xs">{a.action}</TD>
                  <TD>
                    <div className="flex flex-col">
                      <span>{a.subject}</span>
                      <span className="text-ink-muted text-[10px]">{a.subjectType}</span>
                    </div>
                  </TD>
                  <TD className="text-xs">{a.path ?? "—"}</TD>
                  <TD className="text-xs">
                    {/* before -> after reads as the actual transition, so a
                        revoke and a downgrade are not both just "changed". */}
                    {a.levelBefore ?? "none"} → {a.levelAfter ?? "removed"}
                    {a.detail && (
                      <div className="text-ink-muted mt-0.5 text-[10px] italic">
                        {a.detail}
                      </div>
                    )}
                  </TD>
                </TR>
              ))}
            {audit.length === 0 && (
              <TableEmpty colSpan={6}>
                Nothing recorded yet. Entries appear from the next permission change.
              </TableEmpty>
            )}
          </TBody>
        </Table>
      </Card>
    ),
  };

  const sendGridTab: TabItem = {
    key: "sendgrid",
    label: "Email",
    content: (
      <Card
        title="Email notifications"
        description="Used to notify approvers of pending sync requests."
        className="max-w-xl"
      >
        <form onSubmit={handleSaveSendGrid} className="space-y-4">
          {sendGridSaved && <Alert tone="success">Settings saved.</Alert>}

          <Field label="SendGrid API key">
            {(props) => (
              <Input
                {...props}
                type="password"
                value={sendGridSettings.apiKey}
                onChange={(e) =>
                  setSendGridSettings({ ...sendGridSettings, apiKey: e.target.value })
                }
                placeholder="SG.xxxxxxxxxxxx"
                autoComplete="off"
              />
            )}
          </Field>

          <Field
            label="From address"
            hint="Must be a verified sender in your SendGrid account."
          >
            {(props) => (
              <Input
                {...props}
                type="email"
                value={sendGridSettings.fromEmail}
                onChange={(e) =>
                  setSendGridSettings({ ...sendGridSettings, fromEmail: e.target.value })
                }
                placeholder="noreply@yourdomain.com"
              />
            )}
          </Field>

          <Button type="submit" size="md" variant="primary">
            Save configuration
          </Button>
        </form>
      </Card>
    ),
  };

  const tabs = isAdmin
    ? [approvalsTab, usersTab, groupsTab, permissionsTab, ssoTab, auditTab, sendGridTab]
    : [approvalsTab];

  return (
    <div className="flex h-full flex-col p-5">
      <header className="mb-4 shrink-0">
        <h1 className="text-ink text-lg font-semibold tracking-tight">Control panel</h1>
        <p className="text-ink-muted mt-0.5 text-[13px]">
          {isAdmin
            ? "Manage users, groups, catalog permissions and integrations."
            : "Review diagrams awaiting sync approval."}
        </p>
      </header>

      <Tabs items={tabs} defaultActiveKey="approvals" className="flex-1" />

      {/* --- Rename group ---------------------------------------------- */}
      <Modal
        open={showRenameGroupModal}
        onClose={() => setShowRenameGroupModal(false)}
        title="Rename group"
        footer={
          <>
            <Button size="md" variant="ghost" onClick={() => setShowRenameGroupModal(false)}>
              Cancel
            </Button>
            <Button size="md" variant="primary" onClick={handleRenameGroup}>
              Save
            </Button>
          </>
        }
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void handleRenameGroup();
          }}
        >
          <Field label="Group name">
            {(props) => (
              <Input
                {...props}
                data-autofocus
                value={selectedGroup?.name || ""}
                onChange={(e) =>
                  setSelectedGroup(
                    selectedGroup ? { ...selectedGroup, name: e.target.value } : null,
                  )
                }
              />
            )}
          </Field>
          <button type="submit" className="hidden" aria-hidden="true" />
        </form>
      </Modal>

      {/* --- Edit user -------------------------------------------------- */}
      <Modal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        size="md"
        title={`Edit ${currentUser?.username ?? "user"}`}
        footer={
          <>
            <Button size="md" variant="ghost" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button size="md" variant="primary" onClick={handleEditUser}>
              Save changes
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Role">
            {(props) => (
              <Select
                {...props}
                data-autofocus
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
              >
                {ROLE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="Email" hint="Leave blank to fall back to the username.">
            {(props) => (
              <Input
                {...props}
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
              />
            )}
          </Field>

          <Field label="New password" hint="Leave blank to keep the current password.">
            {(props) => (
              <Input
                {...props}
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
              />
            )}
          </Field>

          <div className="border-line space-y-3.5 border-t pt-4">
            <Switch
              checked={isAutoApproved}
              onChange={setIsAutoApproved}
              label="Auto-approve sync requests"
              hint="This user's diagrams skip the approval queue."
            />
            <Switch
              checked={receiveApprovalEmails}
              onChange={setReceiveApprovalEmails}
              disabled={newRole === "user"}
              label="Email me about pending approvals"
              hint={
                newRole === "user"
                  ? "Only approvers and admins can review requests."
                  : "Sent whenever a diagram enters the queue."
              }
            />
          </div>

          <Field
            label="Group memberships"
            hint="Hold Ctrl (or Cmd) to select more than one."
          >
            {(props) => (
              <Select
                {...props}
                multiple
                size={Math.min(Math.max(groups.length, 3), 6)}
                value={editUserGroupIds}
                onChange={(e) =>
                  setEditUserGroupIds(
                    Array.from(e.target.selectedOptions, (option) => option.value),
                  )
                }
              >
                {groups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
      </Modal>

      {/* --- Delete user ------------------------------------------------ */}
      <Modal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        tone="danger"
        dismissOnBackdrop={false}
        title={`Delete ${currentUser?.username ?? "this user"}?`}
        description="Their account and every permission granted to it will be removed. This cannot be undone."
        footer={
          <>
            <Button size="md" variant="ghost" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button size="md" variant="danger" onClick={handleDeleteUser} data-autofocus>
              Delete user
            </Button>
          </>
        }
      />
    </div>
  );
}
